import { isHttpError, isProxyRefusal } from '@ns/plugin-msgraph-proxy-client';
import type { ChoiceState } from '@ns/plugin-picker-shared';
import { describeDroppedRows } from '@ns/plugin-picker-shared';

/** Proxy refusal codes a filler cannot fix by trying again. */
export const PERMANENT_REFUSAL_CODES: ReadonlySet<string> = new Set([
  'consent-revoked',
  'expired',
  'identity-mismatch',
  'no-credential',
]);

/** What the state needs from a page: whether the visible list is complete, and how many rows it left out. */
export interface DirectoryPagePreview {
  /** Rows of the page the picker could not offer. Absent counts as none. */
  readonly droppedRowCount?: number;
  /** Bound Graph continuation, when more results exist beyond the visible page. */
  readonly more?: () => Promise<unknown>;
}

/** Query lifecycle facts mapped into a shared picker state. */
export interface DirectoryQueryStateInput {
  /** The caught request failure, inspected only to identify a timeout. */
  readonly error: unknown;
  /** Marks a locally classified permanent request or response-shape failure. */
  readonly fatal?: boolean;
  /** Whether the active query ended in a failure. */
  readonly isError: boolean;
  /** Whether the active query has not yet settled. */
  readonly isPending: boolean;
  /** The active Graph page, when one has arrived. */
  readonly page?: DirectoryPagePreview;
  /** Retries the same active query without changing its key. */
  readonly refetch?: () => void;
}

/** The ChoiceField state and the evidence available for selection validation. */
export interface DirectoryQueryState {
  /** The lifecycle state rendered by the shared picker field. */
  readonly choiceState: ChoiceState;
  /** Whether the active page proves a missing selected item absent from the collection. */
  readonly isComplete: boolean;
}

/** The failure message for a fatal or timed-out directory query. */
function failureMessage(error: unknown, fatal: boolean): string {
  if (isTimeout(error))
    return 'The directory did not answer in time. Try again.';
  if (fatal)
    return 'The service sent something unexpected, so it was not used.';
  return 'The directory could not be loaded. Try again.';
}

/**
 * Maps an Entra Graph page and query lifecycle to a closed picker presentation state.
 *
 * @param input - The active query lifecycle and page-completeness facts.
 * @returns A shared picker state with explicit collection-completeness evidence.
 */
export function directoryQueryState(
  input: DirectoryQueryStateInput,
): DirectoryQueryState {
  if (input.isError) {
    const fatal = input.fatal === true || isPermanentFailure(input.error);
    return {
      choiceState: {
        kind: 'failed',
        message: failureMessage(input.error, fatal),
        ...(fatal || input.refetch === undefined
          ? {}
          : { retry: input.refetch }),
        severity: fatal ? 'fatal' : 'warning',
      },
      isComplete: false,
    };
  }

  if (input.isPending) {
    return { choiceState: { kind: 'fetching' }, isComplete: false };
  }

  const hasMore = input.page?.more !== undefined;
  const droppedRowCount = input.page?.droppedRowCount ?? 0;
  const notices = [
    ...(hasMore
      ? ['More results are available. Type to narrow the list.']
      : []),
    ...(droppedRowCount > 0 ? [describeDroppedRows(droppedRowCount)] : []),
  ];
  return {
    choiceState:
      notices.length === 0
        ? { kind: 'ready' }
        : { kind: 'ready', notice: notices },
    isComplete: input.page !== undefined && !hasMore,
  };
}

function isTimeout(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'failureKind' in error &&
    error.failureKind === 'timeout'
  );
}

function isPermanentFailure(error: unknown): boolean {
  if (isProxyRefusal(error)) {
    return PERMANENT_REFUSAL_CODES.has(error.body.error.code);
  }

  if (!isHttpError(error)) return false;
  return (
    error.failureKind === 'parse' ||
    error.failureKind === 'validation' ||
    error.failureKind === 'too-large' ||
    error.failureKind === 'unsupported'
  );
}
